import React from "react";
import * as MUI from "@mui/material";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { decimalRules } from "../../../utils/decimalInput";
import { formatCurrency } from "../../../utils/stockStatus";

// Price/PDV fields "in the same manner they exist on an item" - same
// decimal-comma input, netto/brutto-aware labeling, and derived read-only
// total box as WarehouseItemForm's PricingAndStock section, extracted so
// both the add-service form and the edit Pricing tab can share it instead
// of duplicating the netto/brutto/decimal logic twice.
export default function ServicePriceFields({
  control,
  errors,
  priceEntryMode,
  derivedPrice,
  currency,
}) {
  const { t } = useTranslation();

  return (
    <MUI.Stack spacing={2.5}>
      <MUI.Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2.5,
        }}
      >
        <Controller
          control={control}
          name="price"
          rules={decimalRules(t, { min: 0 })}
          render={({ field }) => (
            <MUI.TextField
              {...field}
              type="text"
              inputMode="decimal"
              label={t(priceEntryMode === "BRUTTO" ? "PriceWithVat" : "Price")}
              fullWidth
              size="small"
              error={!!errors?.price}
              helperText={
                errors?.price?.message ??
                t(priceEntryMode === "BRUTTO" ? "PriceIncludesVat" : "PriceExcludesVat")
              }
            />
          )}
        />
        <Controller
          control={control}
          name="pdv"
          rules={decimalRules(t, { min: 0, max: 100 })}
          render={({ field }) => (
            <MUI.TextField
              {...field}
              type="text"
              inputMode="decimal"
              label={t("PDV")}
              fullWidth
              size="small"
              error={!!errors?.pdv}
              helperText={errors?.pdv?.message}
            />
          )}
        />
      </MUI.Box>

      {/* Always rendered (not conditional on derivedPrice) so the form
          doesn't resize the moment the user finishes typing both fields -
          it shows a placeholder dash until there's a value to derive. */}
      <MUI.Box
        sx={{
          px: 2,
          py: 1.5,
          borderRadius: 2.5,
          bgcolor: (theme) => MUI.alpha(theme.palette.primary.main, 0.08),
          border: "1px solid",
          borderColor: (theme) => MUI.alpha(theme.palette.primary.main, 0.24),
        }}
      >
        <MUI.Typography variant="caption" color="text.secondary">
          {t(priceEntryMode === "BRUTTO" ? "Price" : "PriceWithVat")}
        </MUI.Typography>
        <MUI.Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
          {derivedPrice !== null ? formatCurrency(derivedPrice, currency) : "—"}
        </MUI.Typography>
      </MUI.Box>
    </MUI.Stack>
  );
}
